import React from 'react';
import { ColumnSpan } from '../types';

interface NodeColumnWidthEntry {
    span: ColumnSpan;
    width: number;
}

interface NodeColumnWidthContextType {
    nodeColumnWidths: Record<string, NodeColumnWidthEntry[]>;
    setNodeColumnWidth: (nodeId: string, entry: NodeColumnWidthEntry) => void;
    getNodeColumnWidth: (nodeId: string, span: ColumnSpan) => number | undefined;
}

const NodeColumnWidthContext = React.createContext<NodeColumnWidthContextType | undefined>(undefined);

export const NodeColumnWidthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [nodeColumnWidths, setNodeColumnWidths] = React.useState<Record<string, NodeColumnWidthEntry[]>>({});

    const setNodeColumnWidth = React.useCallback((nodeId: string, entry: NodeColumnWidthEntry) => {
        setNodeColumnWidths(prev => {
            const nodeEntries = prev[nodeId] ?? [];
            const existingEntry = nodeEntries.find(e => e.span === entry.span);

            if (existingEntry) {
                existingEntry.width = entry.width;
            } else {
                nodeEntries.push(entry);
            }

            return { ...prev, [nodeId]: [...nodeEntries] };
        });
    }, []);

    const getNodeColumnWidth = (nodeId: string, span: ColumnSpan): number | undefined => {
        return nodeColumnWidths[nodeId]?.find(e => e.span === span)?.width;
    };

    return (
        <NodeColumnWidthContext.Provider value={{ nodeColumnWidths, setNodeColumnWidth, getNodeColumnWidth }}>
            {children}
        </NodeColumnWidthContext.Provider>
    );
};

export const useNodeColumnWidth = (): NodeColumnWidthContextType => {
    const context = React.useContext(NodeColumnWidthContext);

    if (!context) {
        throw new Error('useNodeColumnWidth must be used within a NodeColumnWidthProvider');
    }

    return context;
};
